import { AxiosResponse } from 'axios';
import api from '../../utils/api';
import { PAGE_SIZE } from '~/constants';
import { Pagination } from '~/types';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { extractTextFromHTML } from '~/utils/extractTextFromHTML';
import {
  PrivacyPolicy,
  PrivacyPolicyStatus,
  PrivacyPolicyStatusClient,
  PrivacyPolicyView,
} from '~/types/privacyPolicy';

export async function getPrivacyPolicy(
  page: number,
  page_size?: number,
  situacao?: string,
  inicio?: string,
  fim?: string,
  assunto?: string,
  order = 'DESC',
  order_by = 'data_publicacao',
): Promise<Pagination<PrivacyPolicyView>> {
  const { data } = await api.get(`/politica_privacidade`, {
    params: {
      ...(situacao && { status: situacao }),
      ...(inicio && { data_inicial: inicio }),
      ...(fim && { data_final: fim }),
      ...(assunto && { search: assunto }),
      page,
      size: page_size || PAGE_SIZE,
      order,
      order_by,
    },
  });

  const aux: PrivacyPolicyView = data.results.map((item: PrivacyPolicy) => {
    return {
      ...item,
      formattedStatus:
        item.status === PrivacyPolicyStatus.PUBLICADO
          ? 'Publicado'
          : item.status === PrivacyPolicyStatus.RASCUNHO
          ? 'Rascunho'
          : 'Cancelado',
      statusColor:
        item.status === PrivacyPolicyStatus.PUBLICADO
          ? 'messenger'
          : item.status === PrivacyPolicyStatus.RASCUNHO
          ? 'purple'
          : 'red',
      formattedPublicacao: item.data_publicacao
        ? formatInTimeZone(`${item.data_publicacao}`, 'dd/MM/yyyy')
        : '-',
      formattedCriacao: item.data_criacao
        ? formatInTimeZone(`${item.data_criacao}`, 'dd/MM/yyyy')
        : '-',
      formattedMensage:
        extractTextFromHTML(item.mensagem).length > 100
          ? `${item.mensagem.substring(0, 100)}...`
          : item.mensagem,
      formattedMessageTooltip: extractTextFromHTML(item.mensagem),
    };
  });

  return { ...data, results: aux };
}

export async function getPrivacyPolicyControl(): Promise<PrivacyPolicyView> {
  const { data } = await api.get(
    `/politica_privacidade_usuario/get_politica_privacidade`,
  );

  const aux: PrivacyPolicyView = {
    ...data,
    formattedStatus:
      data.status === PrivacyPolicyStatusClient.LIDO ? 'Lido' : 'Não lido',
    statusColor:
      data.status === PrivacyPolicyStatusClient.LIDO ? 'messenger' : 'purple',
    formattedPublicacao: data.data_publicacao
      ? formatInTimeZone(`${data.data_publicacao}`, "dd/MM/yyyy 'às' HH:mm")
      : '-',
    formattedCriacao: data.data_criacao
      ? formatInTimeZone(`${data.data_criacao}`, "dd/MM/yyyy 'às' HH:mm")
      : '-',
    formattedMensage:
      extractTextFromHTML(data.mensagem).length > 100
        ? `${data.mensagem.substring(0, 100)}...`
        : data.mensagem,
    formattedMessageTooltip: extractTextFromHTML(data.mensagem),
  };

  return aux;
}

export async function getActualPrivacyPolicy(): Promise<PrivacyPolicyView> {
  const { data } = await api.get(
    `/politica_privacidade_usuario/get_politica_privacidade_vigente`,
  );

  const aux: PrivacyPolicyView = {
    ...data,
    formattedStatus:
      data.status === PrivacyPolicyStatus.PUBLICADO
        ? 'Publicado'
        : data.status === PrivacyPolicyStatus.RASCUNHO
        ? 'Rascunho'
        : 'Cancelado',
    statusColor:
      data.status === PrivacyPolicyStatus.PUBLICADO
        ? 'messenger'
        : data.status === PrivacyPolicyStatus.RASCUNHO
        ? 'purple'
        : 'red',
    formattedPublicacao: data.data_publicacao
      ? formatInTimeZone(`${data.data_publicacao}`, "dd/MM/yyyy 'às' HH:mm")
      : '-',
    formattedCriacao: data.data_criacao
      ? formatInTimeZone(`${data.data_criacao}`, "dd/MM/yyyy 'às' HH:mm")
      : '-',
    formattedMensage:
      extractTextFromHTML(data.mensagem).length > 100
        ? `${data.mensagem.substring(0, 100)}...`
        : data.mensagem,
    formattedMessageTooltip: extractTextFromHTML(data.mensagem),
  };

  return aux;
}

export async function setPrivacyPolicyRead(
  id_politica_privacidade: number,
): Promise<PrivacyPolicy> {
  const { data } = await api.post(`/politica_privacidade_usuario`, {
    id_politica_privacidade,
  });

  return data;
}

export async function getPrivacyPolicyById(
  id: string,
): Promise<PrivacyPolicyView> {
  const { data } = await api.get(`/politica_privacidade/${id}`);

  return data;
}

export async function cratePrivacyPolicy(
  atualizacao: PrivacyPolicy,
): Promise<PrivacyPolicy> {
  const { data } = await api.post(`/politica_privacidade`, atualizacao);

  return data;
}

export async function updatePrivacyPolicy(
  atualizacao: PrivacyPolicy,
): Promise<PrivacyPolicy> {
  const { data } = await api.put(
    `/politica_privacidade/${atualizacao.id}`,
    atualizacao,
  );

  return data;
}

export async function deletePrivacyPolicy(id: number): Promise<void> {
  await api.delete(`/politica_privacidade/${id}`);
}

export async function cancelPrivacyPolicy(
  id: number,
  motivo: string,
): Promise<void> {
  await api.put(`/politica_privacidade/${id}/cancelar`, {
    motivo_cancelamento: motivo,
  });
}

export async function publishPrivacyPolicy(id: number): Promise<void> {
  await api.put(`/politica_privacidade/${id}/publicar`);
}

export async function downloadFiles(
  id: number | string,
): Promise<AxiosResponse> {
  return api.get(`/politica_privacidade/${id}/download_arquivos`, {
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function downloadFile(
  id: number | string,
): Promise<AxiosResponse> {
  return api.get(`/politica_privacidade/${id}/exportar_relatorio`, {
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}

export async function existPrivacyPolicyDraft(): Promise<boolean> {
  const { data } = await api.get(
    `/politica_privacidade/existe_politica_privacidade_rascunho`,
  );

  return data;
}
